import type { SubtitleOption } from '../settings-menu/settings-menu.types';
import type React from 'react';

import classNames from 'classnames';
import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDebouncedCallback } from '../../hooks/use-debounced-callback';
import { ChevronIcon } from '../../icons/chevron';
import { CloseIcon } from '../../icons/close';
import { DownloadIcon } from '../../icons/download';
import { SearchIcon } from '../../icons/search';
import { downloadFile } from '../../utils/download-file';
import { TextInput } from '../text-input/text-input';
import { Tooltip } from '../tooltip/tooltip';

import styles from './transcript-header.module.css';

type TranscriptHeaderProps = {
  languages?: SubtitleOption[];
  selectedLanguage?: string;
  onSelectedLanguageChange?: (language: string) => void;
  onClose?: () => void;
  onSearch: (search: string) => void;
  searchResultCount: number | undefined;
  currentSearchResult: number | undefined;
  setCurrentSearchResult: (index: number) => void;
  showTranscriptDownload?: boolean;
};

export function TranscriptHeader({
  languages,
  selectedLanguage,
  onClose,
  onSearch,
  searchResultCount,
  currentSearchResult,
  setCurrentSearchResult,
  showTranscriptDownload,
}: TranscriptHeaderProps) {
  const { t } = useTranslation();

  const [search, setSearch] = useState<string>('');
  const [searchVisible, setSearchVisible] = useState<boolean>(false);

  const toggleSearch = useCallback(() => {
    setSearchVisible((state) => !state);
  }, []);

  const handleOnDownloadClick = useCallback(() => {
    const src = languages?.find((language) => language.id === selectedLanguage)?.src;
    if (src) {
      downloadFile(src);
    }
  }, [languages, selectedLanguage]);

  const debouncedOnSearch = useDebouncedCallback(
    useCallback((e: ChangeEvent<HTMLInputElement>) => onSearch(e.target.value), [onSearch]),
    500
  );

  const clearSearch = useCallback(() => {
    setSearch('');
    onSearch('');
  }, [onSearch]);

  const handleInputOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      if (e.target.value) {
        debouncedOnSearch(e);
      } else {
        clearSearch();
      }
    },
    [clearSearch, debouncedOnSearch]
  );

  const incrementSearchResult = useCallback(
    (increment: number) => {
      if (currentSearchResult === undefined || searchResultCount === undefined) {
        return;
      }

      let nextSearchResult = currentSearchResult + increment;
      if (nextSearchResult < 1) {
        nextSearchResult = searchResultCount ?? 0;
      } else if (nextSearchResult > (searchResultCount ?? 0)) {
        nextSearchResult = Math.min(1, searchResultCount ?? 0);
      }
      setCurrentSearchResult(nextSearchResult);
    },
    [currentSearchResult, searchResultCount, setCurrentSearchResult]
  );

  const handlePreviousSearchResult = useCallback(() => incrementSearchResult(-1), [incrementSearchResult]);
  const handleNextSearchResult = useCallback(() => incrementSearchResult(1), [incrementSearchResult]);

  const handleInputOnKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleNextSearchResult();
      }
    },
    [handleNextSearchResult]
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>{t('ui.menu.item.transcript')}</span>
        <div className={styles.controls}>
          <Tooltip
            placement="bottom"
            tooltip={t('ui.transcript.menu.search.placeholder')}
            trigger={(ref, props) => (
              <button
                className={classNames(styles.button, styles.iconButton, styles.searchToggle)}
                onClick={toggleSearch}
                ref={ref}
                {...props}
              >
                <SearchIcon />
              </button>
            )}
          />

          {showTranscriptDownload && (
            <Tooltip
              placement="bottom"
              tooltip={t('ui.transcript.menu.download.button.label')}
              trigger={(ref, props) => (
                <button
                  className={classNames(styles.button, styles.iconButton)}
                  onClick={handleOnDownloadClick}
                  data-testid="transcript-download-button"
                  ref={ref}
                  {...props}
                >
                  <DownloadIcon />
                </button>
              )}
            />
          )}

          <Tooltip
            placement="bottom"
            tooltip={t('ui.transcript.menu.close.button.label')}
            trigger={(ref, props) => (
              <button
                className={classNames(styles.button, styles.iconButton)}
                onClick={onClose}
                data-testid="transcript-close-button"
                ref={ref}
                {...props}
              >
                <CloseIcon />
              </button>
            )}
          />
        </div>
      </div>
      <div className={classNames(styles.search, searchVisible && styles.searchVisible)}>
        <TextInput
          key="transcript-search"
          data-testid="transcript-search"
          value={search}
          onChange={handleInputOnChange}
          onKeyDown={handleInputOnKeyDown}
          placeholder={t('ui.transcript.menu.search.placeholder')}
          className={styles.searchInput}
          rightIcon={
            <div className={styles.searchIcons}>
              {searchResultCount !== undefined && (
                <>
                  <span className={styles.searchResultCount}>
                    {currentSearchResult} / {searchResultCount}
                  </span>
                  <div className={styles.searchPrevNext}>
                    <button onClick={handlePreviousSearchResult} className={styles.iconButton}>
                      <ChevronIcon className={styles.chevronUp} />
                    </button>
                    <button onClick={handleNextSearchResult} className={styles.iconButton}>
                      <ChevronIcon className={styles.chevronDown} />
                    </button>
                  </div>
                </>
              )}
              <div className={styles.separator} />

              {search ? (
                <button onClick={clearSearch} className={classNames(styles.iconButton, styles.clearSearch)}>
                  <CloseIcon />
                </button>
              ) : (
                <div className={styles.searchIndicatorIcon}>
                  <SearchIcon />
                </div>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
}
